/**
 * PERSONAL AIRCRAFT and the paint shop behind it.
 *
 * No capture of either screen exists (`docs/game/ui-reference/index.md`'s
 * session never got past enlistment), so these are built from the manual's own
 * description and from the parts the captured screens measure — the same
 * standing this repo gives the AERODROME menu.
 *
 * > After reaching the rank of Captain or Rittmeister, you will be given the
 * > use of a personal aircraft. On the Personal Aircraft screen you may view
 * > your plane, request a new aircraft and paint your plane. The arrow keys on
 * > the bottom left of the screen allow you to change the view of your
 * > aircraft. You can zoom the camera in/out and rotate it left/right. …
 * > Pressing Paint Aircraft will bring up the painting menu. You may paint two
 * > sections of your aircraft: Wings and Fuselage. Select which part of your
 * > plane you wish to paint and then the color you wish to paint it.
 *
 * So: one screen with a turntable and three buttons, and a `PAINT WHAT?` /
 * `WHICH COLOR?` pair over it. `WHICH COLOR?` offers the eight names `PS.EXE`
 * ships, which are slots 0-7 of `RB.EXE`'s paint table
 * (docs/formats/shading-and-paint.md §10) — not a colour picker, because the
 * original's colours are ramp anchors and an arbitrary RGB has no ramp.
 */

import { aircraftLabel, PAINT_COLORS, type AircraftKey, type PaintColor } from "@red-opus/engine";
import type { Rect } from "../render/draw-ops.ts";
import { measureText, type FontAtlas } from "../render/text-metrics.ts";
import type { MenuAction } from "./menu-model.ts";
import { actionRow, wrapText, type ActionSpec } from "./panel-parts.ts";
import type { ListRow, ShellScreen, Widget } from "./shell-screen.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

/** The turntable's well: the wide plate the panel screens use, with the caption strip left under it. */
export const PREVIEW_RECT: Rect = { x: 20, y: 34, w: 280, h: 96 };

/** The four view arrows the manual puts "on the bottom left of the screen". */
const ARROW_ROW_Y = 136;
const ARROW_W = 22;
const ARROW_H = 16;
const ARROW_X = 18;
const ARROW_PITCH = ARROW_W + 2;
/** Where the caption plate can start without running under the fourth arrow. */
const CAPTION_X = ARROW_X + 4 * ARROW_PITCH + 4;
const CAPTION_W = 302 - CAPTION_X;
const CAPTION_PAD = 5;

function arrows(): Widget[] {
  const labels: readonly { readonly id: string; readonly label: string }[] = [
    { id: "rotate-left", label: "<" },
    { id: "rotate-right", label: ">" },
    { id: "zoom-in", label: "+" },
    { id: "zoom-out", label: "-" },
  ];
  return labels.map((entry, index) => ({
    kind: "button",
    id: entry.id,
    rect: { x: ARROW_X + index * ARROW_PITCH, y: ARROW_ROW_Y, w: ARROW_W, h: ARROW_H },
    label: entry.label,
    enabled: true,
    action: command(`view:${entry.id}`),
  }));
}

export interface PersonalAircraftScreenSpec {
  readonly aircraft: AircraftKey;
  /** The paint as it stands, for the caption under the turntable. */
  readonly wings: PaintColor | undefined;
  readonly fuselage: PaintColor | undefined;
  /** False while the pilot is inside the three-month tenure or otherwise refused. */
  readonly canRequest: boolean;
  readonly background: string | undefined;
}

/**
 * `"Fokker Dr.I: wings Blue, fuselage Black"`, or the plain type while it is in
 * factory colours.
 *
 * The strip is one line of a fixed width, so a caption that would run past it
 * drops the aircraft's name rather than being clipped mid-word — the type is
 * already the thing on screen, and the colours are what the pilot came to read.
 * (Punctuation is plain ASCII: `FONT3` is the original's atlas and draws an em
 * dash as a blank.)
 */
export function paintCaption(spec: PersonalAircraftScreenSpec, font?: FontAtlas): string {
  const parts = [spec.wings ? `wings ${spec.wings}` : "", spec.fuselage ? `fuselage ${spec.fuselage}` : ""].filter(
    (part) => part !== "",
  );
  const paint = parts.length === 0 ? "squadron colours" : parts.join(", ");
  const full = `${aircraftLabel(spec.aircraft)}: ${paint}`;
  if (!font || measureText(font, full) <= CAPTION_W - 2 * CAPTION_PAD) return full;
  return paint;
}

export function personalAircraftScreen(spec: PersonalAircraftScreenSpec, font: FontAtlas, heading: FontAtlas): ShellScreen {
  const actions: ActionSpec[] = [
    { id: "back", label: "CONTINUE", action: command("aerodrome") },
    { id: "request", label: "REQUEST NEW AIRCRAFT", action: command("request-aircraft"), enabled: spec.canRequest },
    { id: "paint", label: "PAINT AIRCRAFT", action: command("paint") },
  ];
  return {
    id: "career/aircraft",
    chrome: { kind: "panel", title: "PERSONAL AIRCRAFT", emblem: "none", background: spec.background },
    widgets: [
      { kind: "well", rect: PREVIEW_RECT },
      ...arrows(),
      { kind: "plate", rect: { x: CAPTION_X, y: ARROW_ROW_Y, w: CAPTION_W, h: ARROW_H } },
      { kind: "text", text: paintCaption(spec, font), x: CAPTION_X + CAPTION_PAD, y: ARROW_ROW_Y + 4, role: "label" },
      ...actionRow(actions, heading, 158),
    ],
    cancel: command("aerodrome"),
    viewport: PREVIEW_RECT,
  };
}

/** The two parts `PAINT WHAT?` offers, plus the original's paint-both shortcut. */
export const PAINT_PARTS = ["wings", "fuselage", "everything"] as const;
export type PaintPart = (typeof PAINT_PARTS)[number];

const SWATCH: Rect = { x: 0, y: 0, w: 22, h: 12 };

/**
 * `WHICH COLOR?` — the eight names down one column with the palette colour each
 * one paints shown beside it. The swatch is the honest part of this screen: the
 * names are the original's, but "Black" is palette 70, a dark grey, and seeing
 * that before committing is worth the two extra columns the original had no
 * room for.
 */
export function paintMenuScreen(part: PaintPart, swatches: readonly number[], heading: FontAtlas): ShellScreen {
  const plateTop = 24;
  const top = 44;
  const pitch = 14;
  const rowHeight = 12;
  // The plate has to reach past the *last row*, not past `count * pitch` from
  // its own top — the two differ by the gap above the first row, which is what
  // made the first cut of this screen spill its bottom row onto the backdrop.
  const plateBottom = top + (PAINT_COLORS.length - 1) * pitch + rowHeight + 6;
  const widgets: Widget[] = [
    { kind: "plate", rect: { x: 60, y: plateTop, w: 200, h: plateBottom - plateTop } },
    { kind: "text", text: `PAINT WHAT? ${part.toUpperCase()}`, x: 68, y: 28, role: "prompt" },
  ];
  PAINT_COLORS.forEach((color, index) => {
    const y = top + index * pitch;
    widgets.push({
      kind: "button",
      id: `color:${color}`,
      rect: { x: 68, y, w: 120, h: rowHeight },
      label: color.toUpperCase(),
      enabled: true,
      action: command(`color:${color}`),
    });
    widgets.push({
      kind: "well",
      rect: { x: 198, y, w: SWATCH.w, h: SWATCH.h },
      face: swatches[index] ?? 0,
    });
  });
  return {
    id: "career/paint-colour",
    chrome: { kind: "panel", title: "WHICH COLOR?", emblem: "none", background: undefined },
    widgets: [...widgets, ...actionRow([{ id: "cancel", label: "CANCEL", action: command("paint") }], heading, 178)],
    cancel: command("paint"),
  };
}

/**
 * REQUEST NEW AIRCRAFT — the types the squadron will hand over this month, or
 * the squadron's own refusal when it will not. The refusal wording is `PS.EXE`'s
 * (`career-actions.ts` fills its slots), so the rank gate and the three-month
 * tenure both speak in the game's voice rather than the port's.
 */
export function requestAircraftScreen(
  selection: { readonly listIndex: number; readonly listTop: number },
  rows: readonly ListRow[],
  message: string,
  font: FontAtlas,
  heading: FontAtlas,
): ShellScreen {
  const offered = rows.length > 0;
  return {
    id: "career/request-aircraft",
    chrome: { kind: "panel", title: "SELECT AIRCRAFT", emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: { x: 16, y: 24, w: 286, h: 118 } },
      { kind: "prose", block: { lines: wrapText(font, message, 268), x: 27, y: 28, role: "label", pitch: 9 } },
      ...(offered
        ? [
            {
              kind: "list" as const,
              id: "rows",
              rect: { x: 28, y: 58, w: 262, h: 78 },
              rows,
              selected: selection.listIndex,
              top: selection.listTop,
              action: command("take-aircraft"),
            },
          ]
        : []),
      ...actionRow(
        [
          { id: "cancel", label: "CANCEL", action: command("aerodrome") },
          { id: "take", label: "CHOOSE", action: command("take-aircraft"), enabled: offered },
        ],
        heading,
        158,
      ),
    ],
    cancel: command("aerodrome"),
  };
}

/** `PAINT WHAT?` — wings, fuselage or both, then straight into `WHICH COLOR?`. */
export function paintPartScreen(heading: FontAtlas): ShellScreen {
  const plateTop = 32;
  const top = 52;
  const pitch = 20;
  const rowHeight = 16;
  const plateBottom = top + (PAINT_PARTS.length - 1) * pitch + rowHeight + 6;
  const widgets: Widget[] = [
    { kind: "plate", rect: { x: 76, y: plateTop, w: 168, h: plateBottom - plateTop } },
    { kind: "text", text: "PAINT WHAT?", x: 84, y: 36, role: "prompt" },
    ...PAINT_PARTS.map((part, index): Widget => ({
      kind: "button",
      id: `part:${part}`,
      rect: { x: 84, y: top + index * pitch, w: 152, h: rowHeight },
      label: part.toUpperCase(),
      enabled: true,
      action: command(`part:${part}`),
    })),
  ];
  return {
    id: "career/paint-part",
    chrome: { kind: "panel", title: "PAINT AIRCRAFT", emblem: "none", background: undefined },
    widgets: [...widgets, ...actionRow([{ id: "cancel", label: "CANCEL", action: command("aircraft") }], heading, 178)],
    cancel: command("aircraft"),
  };
}
